import { Component, computed, DestroyRef, ElementRef, inject, input, output, signal } from '@angular/core';
import { Location } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { UserActionAuthService } from '@core/services/user-action-auth.service';

export interface FormProcessButton {
  slug: string;
  icon?: string;
  method: () => void;
  show?: boolean;
  disabled?: boolean | (() => boolean);
  children?: { slug: string; method: () => void; disabled?: boolean | (() => boolean) }[];
  /** When set, the item is hidden unless the current role has this auth_num granted. */
  authNum?: string;
}

export interface FormButton {
  slug: string;
  icon?: string;
  method: () => void;
  show?: boolean;
  disabled?: boolean | (() => boolean);
}

@Component({
  selector: 'kl-form-container',
  standalone: false,
  templateUrl: './kl-form-container.html',
  styleUrl: './kl-form-container.scss',
})
export class KlFormContainer {
  private readonly location = inject(Location);
  private readonly translate = inject(TranslateService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly actionAuth = inject(UserActionAuthService);

  readonly fullscreen = signal(false);

  readonly mode = input<'add' | 'edit' | 'copy' | 'view' | string>('');
  readonly title = input('');
  readonly saveHidden = input(false);
  /** When false, disables the toolbar Save / Edit button (e.g. no dirty rows). */
  readonly saveDisabled = input(false);
  /** When true, disables all custom toolbar `buttons` (in addition to each button's own `disabled`). */
  readonly customButtonsDisabled = input(false);
  readonly saveAndNew = input(true);
  readonly showToolbar = input(true);
  readonly fullScreen = input(false);
  /** When false, the in-form title block is hidden (use when the dialog/page header already shows the title). */
  readonly showTitle = input(true);
  /** When true, `onBack` only emits `goBack`; skips `Location.back()` (e.g. dialog context). */
  readonly skipLocationBack = input(false);
  /** When true, shows the İşlemler menu in view mode too (e.g. Sales Invoice read-only info buttons). */
  readonly forceShowProcessButtons = input(false);

  readonly alertType = input<'error' | 'success' | 'warn' | 'info'>('error');
  readonly alertMessage = input('');
  readonly alertShow = input(false);

  readonly loading = input(false);
  readonly loadingText = input('');

  readonly processButtons = input<FormProcessButton[]>([]);
  readonly buttons = input<FormButton[]>([]);

  readonly commitSave = output<string | null>();
  readonly closeAlert = output<void>();
  readonly goBack = output<void>();
  readonly fullscreenChange = output<boolean>();

  readonly fullscreenLabelKey = computed(() => (this.fullscreen() ? 'exit_fullscreen' : 'fullscreen'));

  readonly fullscreenIcon = computed(() => (this.fullscreen() ? 'ph ph-corners-in' : 'ph ph-corners-out'));

  constructor() {
    const syncFullscreen = (): void => {
      const active =
        document.fullscreenElement ??
        (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement;
      const isActive = active === this.host.nativeElement;
      this.fullscreen.set(isActive);
      this.fullscreenChange.emit(isActive);
    };

    document.addEventListener('fullscreenchange', syncFullscreen);
    document.addEventListener('webkitfullscreenchange', syncFullscreen);

    this.destroyRef.onDestroy(() => {
      document.removeEventListener('fullscreenchange', syncFullscreen);
      document.removeEventListener('webkitfullscreenchange', syncFullscreen);
      if (
        document.fullscreenElement === this.host.nativeElement ||
        (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement ===
          this.host.nativeElement
      ) {
        void this.exitNativeFullscreen();
      }
    });
  }

  readonly isEditable = computed(() => ['add', 'edit', 'copy', 'return', 'estate'].includes(this.mode()));

  readonly saveLabel = computed(() => (this.mode() === 'edit' ? 'edit' : 'save'));

  readonly alertVisualType = computed(() => (this.alertType() === 'warn' ? 'warning' : this.alertType()));

  onSave(): void {
    this.commitSave.emit('SAVE');
  }

  onSaveAndNew(): void {
    this.commitSave.emit('SAVE_NEW');
  }

  onBack(): void {
    this.goBack.emit();
    if (!this.skipLocationBack()) {
      this.location.back();
    }
  }

  onCloseAlert(): void {
    this.closeAlert.emit();
  }

  toggleFullscreen(): void {
    if (this.fullscreen()) {
      void this.exitNativeFullscreen();
      return;
    }

    const el = this.host.nativeElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
    };
    if (el.requestFullscreen) {
      void el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      void el.webkitRequestFullscreen();
    }
  }

  private exitNativeFullscreen(): Promise<void> | void {
    const doc = document as Document & {
      webkitExitFullscreen?: () => Promise<void> | void;
    };
    if (document.exitFullscreen) {
      return document.exitFullscreen();
    }
    if (doc.webkitExitFullscreen) {
      return doc.webkitExitFullscreen();
    }
  }

  isButtonDisabled(btn: FormButton): boolean {
    return typeof btn.disabled === 'function' ? btn.disabled() : !!btn.disabled;
  }

  isProcessItemDisabled(disabled: boolean | (() => boolean) | undefined): boolean {
    if (disabled == null) {
      return false;
    }
    return typeof disabled === 'function' ? disabled() : !!disabled;
  }

  /** Toolbar `buttons` icon: full Phosphor class, e.g. `ph ph-floppy-disk`. */
  formButtonIcon(icon: string | undefined): string {
    if (!icon) return '';
    if (icon.startsWith('ph ')) return icon;
    return icon;
  }

  readonly processMenuItems = computed((): MenuItem[] => {
    const t = (key: string) => this.translate.instant(key);
    const items: MenuItem[] = [];
    for (const p of this.processButtons()) {
      if (p.show === false) continue;
      if (p.authNum && !this.actionAuth.canShowAction(p.authNum)) continue;
      if (p.children?.length) {
        items.push({
          label: t(p.slug),
          icon: p.icon?.startsWith('ph ') ? p.icon : undefined,
          items: p.children.map((c) => ({
            label: t(c.slug),
            disabled: this.isProcessItemDisabled(c.disabled),
            command: () => c.method(),
          })),
        });
      } else {
        items.push({
          label: t(p.slug),
          icon: p.icon?.startsWith('ph ') ? p.icon : undefined,
          disabled: this.isProcessItemDisabled(p.disabled),
          command: () => p.method(),
        });
      }
    }
    return items;
  });
}
